import React, { useMemo, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import moment from "moment";
import { FullscreenIcon, Pill } from "lucide-react";
import Spinner1 from "../common/spinners";
import { formatCurrency, truncateText } from "../../utils/utilities";

const PurchasedDrugsTable = ({ data, loading }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLab, setSelectedLab] = useState(null);
  const [show, setShow] = useState(false);
  const itemsPerPage = 5;

  const filteredData = useMemo(
    () =>
      (Array.isArray(data) ? data : []).filter(
        (lab) =>
          lab?.item?.drug?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          lab?.item?.drugGeneric?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
      ),
    [data, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const openModal = (lab) => {
    setSelectedLab(lab);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setSelectedLab(null);
  };

  return (
    <div className="w-100">
      <div className="data-panel-head">
        <div className="data-panel-copy">
          <h5>Purchased drugs</h5>
          <p>See what was purchased, the generic type, amount paid, and current request status.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search purchased drugs"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No purchased drugs found</h6>
            <p>Your medication orders will appear here once they are processed.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th>Drug</th>
                  <th>Generic</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <span className="data-icon-avatar">
                          <Pill size={18} />
                        </span>
                        <div>
                          <div className="data-cell-title text-capitalize">
                            {truncateText(`${item?.item?.drug?.name ?? "---"}`, 40)}
                          </div>
                          <div className="data-cell-meta">
                            {moment(item?.item?.transaction?.createdAt).format("ddd, Do MMM, YYYY")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title text-capitalize">
                        {truncateText(`${item?.item?.drugGeneric?.name ?? "---"}`, 40)}
                      </div>
                      <div className="data-cell-meta text-capitalize">
                        {truncateText(`${item?.item?.drugGeneric?.form ?? "---"}`, 40)}
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title">
                        {formatCurrency(item?.item?.transaction?.amount)}
                      </div>
                      <div className="data-cell-meta">
                        {item?.item?.transaction?.description
                          ? truncateText(`${item?.item?.transaction?.description}`, 24)
                          : "No description"}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`data-status-badge ${
                          item?.status === 0 ? "is-warning" : "is-success"
                        }`}
                      >
                        {item?.status === 0 ? "Awaiting Payment" : "Completed"}
                      </span>
                    </td>
                    <td>
                      {item?.status === 1 ? (
                        <button
                          type="button"
                          className="data-action-btn"
                          onClick={() => openModal(item)}
                          title="View purchase details"
                        >
                          <FullscreenIcon size={17} />
                        </button>
                      ) : (
                        <span className="data-cell-meta">Not ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredData.length > 0 && (
          <div className="data-pagination">
            <Button
              variant="light"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => page - 1)}
            >
              Previous
            </Button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Modal size="lg" show={show} onHide={handleClose} scrollable centered>
        <Modal.Header closeButton />
        <Modal.Body>
          {selectedLab ? (
            <div className="w-100">
              <ul style={{ listStyleType: "none", padding: 0 }}>
                <li className="mb-4">
                  Title: <span>{selectedLab?.item?.labTest?.name}</span>
                </li>
                <Table>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Value</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedLab?.item?.parameters?.map((x) => (
                      <tr key={x?.id}>
                        <td>{x?.name}</td>
                        <td>{x?.value}</td>
                        <td>{x?.reference}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ul>
            </div>
          ) : (
            "No data available"
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PurchasedDrugsTable;
